'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X } from 'lucide-react';

import { Card, Eyebrow, Mono, useIsMobile } from '@/components/dashboard/atoms';
import { getArticlePath, type ContentType } from '@/lib/article-url-utils';
import {
  evaluateToast,
  newCountLabel,
  NEW_ARTICLE_COOLDOWN_MS,
  type RecentArticlesPayload,
} from '@/lib/new-article-toast';

/**
 * NewArticleToast
 *
 * A quiet, token-native corner toast announcing newly published articles.
 * Replaces the old top-anchored `NewContentBanner`, which collided with the
 * DashboardShell TopBar (both `top:0; z-50`) and shouted a misleading count.
 *
 * Behaviour (see `@/lib/new-article-toast` for the unit-tested core):
 *  - Polls every 5 min; the FIRST poll only records the arrival baseline.
 *  - "N new" means new SINCE YOU ARRIVED — not "published in some window".
 *  - Never screams: one coalesced toast (count caps at 9+), it only animates in
 *    on first appearance (later polls update it silently), and a dismiss starts
 *    a 10-minute cooldown so a burst of content can't re-pop in your face.
 *  - Headlines the real story and links straight to it; a quiet Refresh pulls
 *    new content into the current view.
 */

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

interface LatestArticle {
  id: string;
  title: string;
  slug: string;
  contentType: ContentType;
}

interface ApiResponse extends RecentArticlesPayload {
  latestArticle?: LatestArticle;
}

export default function NewArticleToast() {
  const router = useRouter();
  const mobile = useIsMobile();

  const [show, setShow] = useState(false);
  const [count, setCount] = useState(0);
  const [article, setArticle] = useState<LatestArticle | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [headlineHover, setHeadlineHover] = useState(false);
  const [refreshHover, setRefreshHover] = useState(false);

  // Refs (not state) so the polling effect is created ONCE and never tears down
  // on detection — the old banner rebuilt its interval on every new article.
  const lastSeenRef = useRef<number | null>(null); // arrival baseline (null until first poll)
  const cooldownUntilRef = useRef(0); // post-dismiss quiet window
  const latestTsRef = useRef(0); // most recent published ts the API has reported

  const poll = useCallback(async () => {
    try {
      const res = await fetch('/api/latest-article-timestamp');
      if (!res.ok) return;

      const data: ApiResponse = await res.json();
      const payload: RecentArticlesPayload = {
        latestTimestamp: data.latestTimestamp ?? 0,
        recent: data.recent ?? [],
      };
      latestTsRef.current = payload.latestTimestamp;

      // First poll establishes the baseline; we never notify on initial load.
      if (lastSeenRef.current === null) {
        lastSeenRef.current = payload.latestTimestamp || Date.now();
        return;
      }

      const { newCount, show: shouldShow } = evaluateToast({
        payload,
        lastSeenTs: lastSeenRef.current,
        cooldownUntil: cooldownUntilRef.current,
        now: Date.now(),
      });

      if (shouldShow) {
        setCount(newCount);
        if (data.latestArticle) setArticle(data.latestArticle);
        // Already-open stays open → AnimatePresence won't re-animate (no yank).
        setShow(true);
      }
    } catch (err) {
      console.error('[NewArticleToast] poll failed:', err);
    }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [poll]);

  // Mark everything up to now as seen (shared by dismiss / refresh / read).
  const markSeen = () => {
    lastSeenRef.current = latestTsRef.current;
  };

  // Reading the story dismisses the toast — it shouldn't linger announcing the
  // thing you just opened. No cooldown: only genuinely newer content re-shows.
  const handleRead = () => {
    setShow(false);
    markSeen();
  };

  const handleDismiss = () => {
    setShow(false);
    markSeen();
    cooldownUntilRef.current = Date.now() + NEW_ARTICLE_COOLDOWN_MS;
  };

  const handleRefresh = () => {
    setRefreshing(true);
    router.refresh();
    markSeen();
    setTimeout(() => {
      setShow(false);
      setRefreshing(false);
    }, 800);
  };

  const articlePath = article ? getArticlePath(article) : null;
  const headline = article?.title ?? 'New story published';

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            zIndex: 60, // above footer ad / chrome (50); below ⌘K palette (200)
            // Desktop: bottom-RIGHT, clear of the left nav rail (this is a global
            // overlay with no knowledge of the rail's width/collapse state).
            // Mobile (no rail): full-width strip (left+right both set).
            left: mobile ? 12 : undefined,
            right: mobile ? 12 : 24,
            bottom: mobile
              ? 'calc(64px + env(safe-area-inset-bottom) + 12px)' // clear the mobile bottom nav
              : 24,
            width: mobile ? 'auto' : 340,
            maxWidth: 'calc(100vw - 24px)',
            filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.5))',
          }}
        >
          <Card spine glow>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
                padding: '13px 14px',
              }}
            >
              {/* Row 1 — calm amber dot + count eyebrow + dismiss */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  aria-hidden
                  className="animate-pulse"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: 'var(--amber)',
                    boxShadow: '0 0 6px var(--amber)',
                  }}
                />
                <Eyebrow>{newCountLabel(count)}</Eyebrow>
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  onClick={handleDismiss}
                  aria-label="Dismiss"
                  style={{
                    display: 'inline-flex',
                    padding: 2,
                    margin: -2,
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    color: 'var(--slate)',
                    lineHeight: 0,
                  }}
                >
                  <X size={15} />
                </button>
              </div>

              {/* Headline — the real story, links straight to it. */}
              {articlePath ? (
                <Link
                  href={articlePath}
                  onClick={handleRead}
                  onMouseEnter={() => setHeadlineHover(true)}
                  onMouseLeave={() => setHeadlineHover(false)}
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 13.5,
                    fontWeight: 600,
                    lineHeight: 1.35,
                    letterSpacing: '-0.01em',
                    color: headlineHover ? 'var(--amber)' : 'var(--parchment)',
                    textDecoration: 'none',
                    transition: 'color 160ms',
                  }}
                >
                  {headline}
                </Link>
              ) : (
                <span
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: 'var(--parchment)',
                  }}
                >
                  {headline}
                </span>
              )}

              {/* Quiet refresh — pull the new content into the current view. */}
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                onMouseEnter={() => setRefreshHover(true)}
                onMouseLeave={() => setRefreshHover(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  alignSelf: 'flex-start',
                  marginTop: 1,
                  padding: 0,
                  cursor: refreshing ? 'default' : 'pointer',
                  background: 'none',
                  border: 'none',
                  color: refreshHover ? 'var(--parchment)' : 'var(--slate)',
                  transition: 'color 160ms',
                }}
              >
                <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
                <Mono size={10} color="inherit" style={{ letterSpacing: '0.08em' }}>
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </Mono>
              </button>
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
